import { getSpeakerLayoutBorderKey } from '@goldcast/utils/src/media/speakerLayoutConfig';
import type { PipShape, PipPosition } from '@goldcast/utils/src/media/speakerLayoutConfig';
import { SizeType, LayoutType, FitType, SpeakerDirectionType } from '../types/asset-types';
import { BaseConfig, layoutConfigMap } from '../types/speaker-types';
import { resolveNewLayoutKind, computeNewEngine } from './newLayoutEngine';

const newEngineCache = new Map<string, Record<number, BaseConfig[]>>();

// 24 speakers + 1 screenshare slot
const MAX_TOTAL_VIDEOS = 25;

export type LayoutConfigParams = {
  size: SizeType;
  layout: LayoutType;
  fitMode: FitType;
  hideBorders: boolean;
  speakerDirection?: SpeakerDirectionType;
  isNewLayoutsEnabled?: boolean;
  hasScreenshare?: boolean;
  roundCorners?: boolean;
  pipShape?: PipShape;
  pipPosition?: PipPosition;
};

function resolveBucket(params: LayoutConfigParams): Record<number, BaseConfig[]> | undefined {
  const { size, layout, fitMode, hideBorders } = params;
  const borderKey = getSpeakerLayoutBorderKey(!hideBorders);
  return layoutConfigMap[size]?.[layout]?.[fitMode]?.[borderKey] as Record<number, BaseConfig[]> | undefined;
}

/**
 * Eagerly build and cache all speakerCount entries (1..25) for the new runtime engine.
 * Returns undefined when the layout is AUDIOGRAM or DEFAULT (old compiled path handles those).
 */
function resolveNewEngineBucket(params: LayoutConfigParams): Record<number, BaseConfig[]> | undefined {
  const {
    size,
    layout,
    fitMode,
    hideBorders,
    roundCorners = false,
    speakerDirection,
    hasScreenshare = false,
    pipShape,
    pipPosition
  } = params;

  // Quick check: AUDIOGRAM/DEFAULT never route to the new engine.
  if (layout === 'AUDIOGRAM' || layout === 'DEFAULT') {
    return undefined;
  }

  const cacheKey = `${size}|${layout}|${fitMode}|${hideBorders}|${roundCorners}|${speakerDirection ?? ''}|${hasScreenshare}|${pipShape ?? ''}|${pipPosition ?? ''}`;
  let bucket = newEngineCache.get(cacheKey);
  if (bucket) {
    return bucket;
  }

  bucket = {};
  for (let n = 1; n <= MAX_TOTAL_VIDEOS; n++) {
    const resolved = resolveNewLayoutKind({ layout, fitMode, totalVideoCount: n, hasScreenshare });
    if (!resolved) {
      return undefined;
    }
    bucket[n] = computeNewEngine({
      size,
      kind: resolved.kind,
      speakerCount: resolved.speakerCount,
      totalVideoCount: n,
      hideBorders,
      roundCorners,
      speakerDirection,
      pipShape,
      pipPosition,
      hasScreenshare
    });
  }

  newEngineCache.set(cacheKey, bucket);
  return bucket;
}

export function getLayoutConfig(params: LayoutConfigParams & { speakerCount: number }): BaseConfig[] | undefined {
  const { speakerCount, isNewLayoutsEnabled } = params;

  // New engine path — used when the flag is on or the clip has been migrated.
  if (isNewLayoutsEnabled) {
    const bucket = resolveNewEngineBucket(params);
    if (bucket !== undefined) {
      return bucket[speakerCount];
    }
  }

  const bucket = resolveBucket(params);
  if (!bucket) {
    return undefined;
  }
  return bucket[speakerCount];
}

export function getLayoutConfigMap(params: LayoutConfigParams): Record<number, BaseConfig[]> | undefined {
  const { isNewLayoutsEnabled } = params;

  // New engine path.
  if (isNewLayoutsEnabled) {
    const bucket = resolveNewEngineBucket(params);
    if (bucket !== undefined) {
      return bucket;
    }
  }

  const bucket = resolveBucket(params);
  if (!bucket) {
    return undefined;
  }
  return bucket;
}
